import { createApiSlice } from '@/shared/features/query/config/query.build';
import type { ReduxQueryClient } from '@/shared/features/query/domain/interfaces/QueryClient';
import type { AppDispatch } from '@/app/store/store';
import { executeQuery } from '@/shared/features/query/utils/queryUtils';
import { getPageApiBaseUrl } from '@/core/utils/apiUtils';
import type { CustomizationResponseDTO } from '../dto/customization.dto';
import { customizationDtoToProductCustomization } from '../mappers/customization.mapper';
import type { CustomizationRepository, CustomizationParams } from '../../domain/interfaces/CustomizationRepository';
import type { ProductCustomization } from '../../domain/models/ProductCustomization';
import { buildCustomizationQueryString, type CustomizationApiArgs } from '../../utils/buildCustomizationParams';

const customizationApiSlice = createApiSlice({
  clientId: 'customizationApi',
  tagTypes: ['Customization'],
  endpoints: {
    fetchCustomization: {
      url: (args: unknown) => {
        const apiArgs = (args || {}) as CustomizationApiArgs;
        return `${getPageApiBaseUrl()}/page${buildCustomizationQueryString(apiArgs)}`;
      },
      method: 'GET',
      transformResponse: (response: unknown): Record<string, unknown> => {
        const dto = (response ?? {}) as CustomizationResponseDTO;
        return customizationDtoToProductCustomization(dto) as unknown as Record<string, unknown>;
      },
      keepUnusedDataFor: 0,
    },
  },
});

export const customizationApi = customizationApiSlice.queryClient;
export const customizationApiRedux = customizationApiSlice.queryClient as ReduxQueryClient;

export class CustomizationAdapter implements CustomizationRepository {
  constructor(private readonly dispatch: AppDispatch) {}

  async fetchCustomization(designId: string, params: CustomizationParams): Promise<ProductCustomization> {
    const initiateResult = customizationApi.endpoints.fetchCustomization.initiate({
      designId,
      ...params,
    });
    const plain = await executeQuery<Record<string, unknown>>(initiateResult, this.dispatch);
    return plain as unknown as ProductCustomization;
  }
}
